/* eslint-disable no-use-before-define */
/* eslint-disable no-plusplus */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-shadow */
import { useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, InlineNotification } from 'uxd-design-system-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import UserPersonaTypes from '../../../constants/UserPersonaTypes';
import FPResultsPageProps from '../../../types/pages/FPResultsPageProps';
import NotificationContent from '../../../types/NotificationContent';
import FPValueCardCarousel from '../../../components/FinancialPlanning/ValueCardCarousel/FPValueCardCarousel';
import getStatementText from '../../../utils/getStatementText';
import getText from '../../../assets/locale/locale';
import FPValueCardPodium from '../../../components/FinancialPlanning/ValueCardPodium/FPValueCardPodium';
import LocaleContext from '../../../contexts/LocaleContext';
import validTime from '../../../utils/validTime/validTime';

import './FPResultsPage.scss';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import DownloadButton from '../../../components/DownloadButton/DownloadButton';
import CopyLinkButton from '../../../components/CopyLinkButton/CopyLinkButton';
import SendResultsButton from '../../../components/SendResultsButton/SendResultsButton';
import GoToFinancialFoundationsButton from '../../../components/GoToFinancialFoundationsButton/GoToFinancialFoundationsButton';
import getLearnMoreContent from '../../../utils/getLearnMoreContent';
import getLearnMoreLinks from '../../../utils/getLearnMoreLinks';
import FPDnDContainer from '../../../components/FinancialPlanning/DragAndDrop/FPDnDContainer';
import ShareLinkIcon from '../../../components/ShareLinkIcon/ShareLinkIcon';
import SocialMediaButton from '../../../components/SocialMediaButton/SocialMediaButton';
import useQuery from '../../../utils/useQuery/useQuery';
import ResultsObject from '../../../types/ResultsObject';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';
import PlanningTopicsInfo from '../../../components/PlanningTopicsInfo/PlanningTopicsInfo';
import PlanningTopic from '../../../types/PlanningTopic';
import INVESTMENT_DECISION_LIST from '../../../constants/InvestmentDecisionList';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';

const FPResultsPage = (props: FPResultsPageProps) => {
  const {
    contactID,
    contactName,
    financialAdvisorID,
    entityID,
    timestamp,
    sessionId,
    valueList,
    finalResultsIndex,
    restartCallback,
    callerApp,
    displaySendToFAButton,
    faInfo,
    financialPlanningContent,
    userPersona,
  } = props;

  const initialResultsStatementIds = finalResultsIndex.map((index: number) => {
    const results = valueList.find((value) => index === value.indexId);
    return results?.statementId;
  });
  const { dataLayerInfo } = useDataLayerInfo();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationContent, setNotificationContent] =
    useState<NotificationContent>({ type: 'informational', message: '' });
  const [displaySendResults, setDisplaySendResults] = useState(false);
  const [coldContactData, setColdContactData] = useState<ColdContact | null>(
    null
  );
  const [userComments, setUserComments] = useState('');
  const [cardClassName, setCardClassName] = useState('value-card-fp');
  const [finalResultsStatementIds, setFinalResultsStatementIds] = useState(
    initialResultsStatementIds
  );
  const [sendResultsButtonDisabled, setSendResultsButtonDisabled] =
    useState(false);
  const topOfResults = useRef<HTMLHeadingElement>(null);
  const locId = useContext(LocaleContext);
  const resultsJSON = createResultsJSON();
  const displayFAName = !!faInfo.faFirstName && !!faInfo.faLastName;
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;
  const sendResultsToFaText = getText(
    locId,
    'sendResultsToFA',
    'fpResultsPage'
  );
  const faNameDefaultText = getText(locId, 'faDefaultText', 'fpResultsPage');

  const whatsNextSendToFaHeadingText = getText(
    locId,
    'whatNextSendToFaHeading',
    'fpResultsPage'
  );
  const whatsNextSendToFaText = getText(
    locId,
    'whatNextSendToFA',
    'fpResultsPage'
  );
  const yourFaNameDefaultText = getText(
    locId,
    'yourFaDefaultText',
    'fpResultsPage'
  );
  const urlQuery = useQuery();

  const isTouchDevice = () => {
    return 'ontouchstart' in window;
  };

  const isFinancialFoundationsUser = () => {
    return (
      (urlQuery.get('faNo') || faInfo.faNo) &&
      urlQuery.get('conId') &&
      urlQuery.get('utm_source') &&
      urlQuery.get('utm_source')?.toUpperCase() === 'FF'
    );
  };

  const isSendToFAButtonDisplayed = () => {
    return (
      displaySendToFAButton &&
      !isFinancialFoundationsUser() &&
      (!contactID || (contactID && validTime(timestamp)))
    );
  };

  const isFindAnFAButtonDisplayed = () => {
    return (
      callerApp === 'StartingPoint' &&
      (!financialAdvisorID || financialAdvisorID === '0') &&
      (!entityID || entityID === '0')
    );
  };

  const getValueStatementHeadersString = () => {
    const valueStatementHeaderArray = finalResultsStatementIds.map(
      (statementId: number) => {
        const statement = valueList.find(
          (value) => statementId === value.statementId
        );
        return statementId === 15 ? 'Custom Text' : statement?.statementText;
      }
    );
    return valueStatementHeaderArray.join('|');
  };

  useEffect(() => {
    addDataLayerTrigger('value-statements-displayed', {
      'value-statements-as-string': getValueStatementHeadersString(),
    });
  }, [finalResultsStatementIds]);

  const updatedFaInfo = {
    ...dataLayerInfo,
    page_type: 'results-page',
  };

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const pageTitle = getText(locId, 'pageTitle', 'fpResultsPage');
  const pageReferrer = previousPath
    ? `${window.location.origin}${previousPath}${currentLocation.search}`
    : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    pagePath: currentLocation.pathname,
    pageTitle,
    page_referrer: pageReferrer,
    external_referrer:
      window.document.referrer && window.document.referrer.trim() !== ''
        ? window.document.referrer
        : undefined,
    page_url: pageURL,
  };

  useEffect(() => {
    setPreviousPath(currentLocation.pathname);
    addDataLayerTrigger('dataLayer-initialized', updatedFaInfo);
    addDataLayerTrigger('VirtualPageview', virtualPageInfo);
    if (topOfResults.current !== undefined) {
      if (topOfResults.current) {
        topOfResults.current.focus();
      }
    }
    document.title = pageTitle;
    addDataLayerTrigger('view-results', {});

    if (isSendToFAButtonDisplayed()) {
      setDisplaySendResults(true);
    }
    setTimeout(removeFocus, 1000);
  }, []);

  function removeFocus() {
    const heading = document.getElementById('heading-no-focus');
    heading?.removeAttribute('tabIndex');
  }

  function createResultsJSON() {
    const resultsObj: Record<string, ResultsObject> = {};
    finalResultsStatementIds.forEach((result: number, index: number) => {
      const statementId = result;
      const statementText = getStatementText(result, valueList);
      const learnMoreContent = getLearnMoreContent(result, valueList);
      const learnMoreLinks = getLearnMoreLinks(result, valueList);
      const keyName = (index + 1).toString();
      resultsObj[keyName] = {
        statementId,
        statementText,
        learnMoreContent,
        learnMoreLinks,
      };
    });
    return resultsObj;
  }

  const handleDefaultDisplayNameText = () => {
    return sendResultsToFaText.replace('%FA_NAME%', faNameDefaultText);
  };
  const handleDisplayFaNameText = () => {
    if (displayFAName) {
      return sendResultsToFaText.replace('%FA_NAME%', faFullName);
    }
    return handleDefaultDisplayNameText();
  };

  const handleWhatsNextSendToFaHeadingText = () => {
    if (displayFAName) {
      return whatsNextSendToFaHeadingText.replace('%FA_NAME%', faFullName);
    }
    return whatsNextSendToFaHeadingText.replace(
      '%FA_NAME%',
      yourFaNameDefaultText
    );
  };

  const handleWhatsNextSendToFaText = () => {
    if (displayFAName) {
      return whatsNextSendToFaText.replace('%FA_NAME%', faFullName);
    }
    return whatsNextSendToFaText.replace('%FA_NAME%', yourFaNameDefaultText);
  };

  const handleCommentsChange = (sendComments: string) => {
    setUserComments(sendComments);
  };

  return (
    <div data-testid="ResultsPage_fp" className="FPResultsPage">
      {finalResultsStatementIds.length > 0 ? (
        <>
          <div className="youDidIt">
            <div className="banner">
              <h1
                id="heading-no-focus"
                className="heading--semi-bold"
                ref={topOfResults}
                tabIndex={-1}
              >
                {contactName.firstName
                  ? getText(
                      locId,
                      'howYouRankedHeading',
                      'fpResultsPage'
                    ).replace('%CONTACT_FIRSTNAME%', contactName.firstName)
                  : getText(
                      locId,
                      'howYouRankedHeadingContactUnkown',
                      'fpResultsPage'
                    )}
              </h1>
            </div>
            <p>{getText(locId, 'topThreePriorities', 'fpResultsPage')}</p>
            <ol className="visually-hidden">
              <li key={finalResultsStatementIds[0]}>
                {getStatementText(finalResultsStatementIds[0], valueList)}
              </li>
              <li key={finalResultsStatementIds[1]}>
                {getStatementText(finalResultsStatementIds[1], valueList)}
              </li>
              <li key={finalResultsStatementIds[2]}>
                {getStatementText(finalResultsStatementIds[2], valueList)}
              </li>
            </ol>
          </div>
          <div className="results_topThree_fp">
            <div
              className="results_podium"
              data-testid="results_podium_fp"
              aria-hidden="true"
            >
              <FPValueCardPodium
                valueList={valueList}
                finalResults={finalResultsStatementIds}
                cardClassName={cardClassName}
                setCardClassName={setCardClassName}
              />
            </div>
            <div
              className="results_carousel_fp"
              data-testid="results_carousel_fp"
            >
              <FPValueCardCarousel
                valueList={valueList}
                finalResults={finalResultsStatementIds}
                cardClassName={cardClassName}
                setCardClassName={setCardClassName}
              />
            </div>
          </div>
          {notificationOpen ? (
            <div className="inlineNotification_container">
              <InlineNotification
                type={notificationContent.type}
                text={notificationContent.message}
                onClose={() => {
                  setNotificationOpen(false);
                }}
              />
            </div>
          ) : null}
          {financialPlanningContent?.planningTopics ? (
            <div>
              <div className="planning-results">
                {getText(locId, 'selectedPlanningTopis', 'fpResultsPage')}
              </div>
              <div
                className="planning-results-cards"
                data-testid="selected-planning-topics-fp"
              >
                {financialPlanningContent.planningTopics.map(
                  (topic: {
                    topicTitleId: string;
                    topicTitle: string;
                    color: string;
                  }) => (
                    <PlanningTopicsInfo
                      key={topic.topicTitleId}
                      topicTitle={topic.topicTitle}
                      topicTitleId={topic.topicTitleId}
                      color={topic.color}
                    />
                  )
                )}
              </div>
            </div>
          ) : null}

          {userPersona !== UserPersonaTypes.DIRECT_URL ? (
            <div className="edit_priorities_btn">
              <Button
                label={getText(locId, 'editBtnLabel', 'fpResultsPage')}
                icon="edit-pencil"
                onClick={() => {
                  navigate({
                    pathname: getText(locId, 'planningPath', 'app'),
                    search: currentLocation.search,
                  });
                }}
                attributes={{
                  'aria-label': 'Edit your financial priorities',
                  value: '',
                  id: 'edit-planning-topics-button',
                  'data-testid': 'edit-planning-topics-button',
                }}
              />
            </div>
          ) : null}

          {financialPlanningContent?.investorInvolvementCode && (
            <>
              <div className="investment-selection-title">
                {getText(locId, 'investmentSelection', 'fpResultsPage')}
              </div>
              <div className="investment-selection-description">
                <p>
                  {INVESTMENT_DECISION_LIST[
                    financialPlanningContent.investorInvolvementCode as keyof typeof INVESTMENT_DECISION_LIST
                  ]?.decisionStatement || ''}
                </p>
              </div>
            </>
          )}

          {userComments && userComments.trim() !== '' && (
            <>
              <div className="shared-comments">
                <div>{getText(locId, 'sharedComments', 'fpResultsPage')}</div>
              </div>
              <div>
                {userComments.split('\n').map((line: string) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </>
          )}
          <div className="nowWhat">
            <div className="action_btns">
              {displaySendResults ? (
                <SendResultsButton
                  coldContactData={coldContactData}
                  contactName={contactName}
                  setColdContactData={setColdContactData}
                  setNotificationContent={setNotificationContent}
                  setNotificationOpen={setNotificationOpen}
                  userComments={userComments}
                  setUserComments={handleCommentsChange}
                  faInfo={faInfo}
                  sessionId={sessionId}
                  resultsJSON={resultsJSON}
                  sendResultsButtonDisabled={sendResultsButtonDisabled}
                  setSendResultsButtonDisabled={setSendResultsButtonDisabled}
                  buttonText={handleDisplayFaNameText()}
                  planningTopics={financialPlanningContent?.planningTopics.map(
                    (topic: PlanningTopic) => topic.code
                  )}
                  investmentInvolvementCode={
                    financialPlanningContent?.investorInvolvementCode
                  }
                />
              ) : null}

              {!isFinancialFoundationsUser() ? (
                <DownloadButton
                  setNotificationContent={setNotificationContent}
                  setNotificationOpen={setNotificationOpen}
                  displaySendToFAButton={displaySendResults}
                  resultsJSON={resultsJSON}
                  coldContactData={coldContactData}
                  userComments={userComments}
                  planningTopics={financialPlanningContent?.planningTopics.map(
                    (topic: PlanningTopic) => topic.code
                  )}
                  investmentInvolvementCode={
                    financialPlanningContent?.investorInvolvementCode
                  }
                  label={getText(locId, 'downloadResults', 'fpResultsPage')}
                />
              ) : null}

              {isFinancialFoundationsUser() ? (
                <GoToFinancialFoundationsButton
                  setNotificationContent={setNotificationContent}
                  setNotificationOpen={setNotificationOpen}
                  sessionId={sessionId}
                  resultsJSON={resultsJSON}
                  faInfo={faInfo}
                  planningTopics={financialPlanningContent?.planningTopics.map(
                    (topic: PlanningTopic) => topic.code
                  )}
                  investmentInvolvementCode={
                    financialPlanningContent?.investorInvolvementCode
                  }
                />
              ) : null}

              {isFindAnFAButtonDisplayed() ? (
                <a
                  href={getText(locId, 'findFALink', 'fpResultsPage')}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={-1}
                >
                  <Button
                    label={getText(locId, 'findFAButton', 'fpResultsPage')}
                    onClick={() => {}}
                    attributes={{
                      value: '',
                      id: 'find-fa',
                      'data-testid': 'find-fa',
                    }}
                  />
                </a>
              ) : null}
              <Button
                label={getText(locId, 'restartQuiz', 'fpResultsPage')}
                onClick={() => {
                  addDataLayerTrigger('restart-quiz', {});
                  restartCallback();
                }}
                attributes={{
                  value: '',
                  id: 'restart-button',
                }}
                styleVariant="secondary"
              />
            </div>
          </div>
          <div className="dnd-header">
            <h3>{getText(locId, 'dndHeading', 'fpResultsPage')}</h3>
            <p>{getText(locId, 'dndMessage', 'fpResultsPage')}</p>
          </div>
          <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
            <FPDnDContainer
              finalResultsStatementIds={finalResultsStatementIds}
              valueList={valueList}
              setFinalResultsStatementIds={setFinalResultsStatementIds}
            />
          </DndProvider>
        </>
      ) : null}
      {!isFinancialFoundationsUser() ? (
        <div className="whatNext">
          <h2>{getText(locId, 'whatNext', 'fpResultsPage')}</h2>
          <div className="cta_container">
            {displaySendResults ? (
              <div className="whatNext_container">
                <h3>{handleWhatsNextSendToFaHeadingText()}</h3>
                <p>{handleWhatsNextSendToFaText()}</p>
                <div className="action_btns">
                  <SendResultsButton
                    coldContactData={coldContactData}
                    contactName={contactName}
                    setColdContactData={setColdContactData}
                    setNotificationContent={setNotificationContent}
                    setNotificationOpen={setNotificationOpen}
                    userComments={userComments}
                    setUserComments={setUserComments}
                    faInfo={faInfo}
                    sessionId={sessionId}
                    resultsJSON={resultsJSON}
                    sendResultsButtonDisabled={sendResultsButtonDisabled}
                    setSendResultsButtonDisabled={setSendResultsButtonDisabled}
                    buttonText={handleDefaultDisplayNameText()}
                    planningTopics={financialPlanningContent?.planningTopics.map(
                      (topic: PlanningTopic) => topic.code
                    )}
                    investmentInvolvementCode={
                      financialPlanningContent?.investorInvolvementCode
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="whatNext_container">
                <h3>
                  {getText(locId, 'whatNextGetMatchedHeading', 'fpResultsPage')}
                </h3>
                <p>{getText(locId, 'whatNextGetMatched', 'fpResultsPage')}</p>
                <div className="action_btns">
                  <a
                    href={getText(locId, 'whatNextMatchLink', 'fpResultsPage')}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={-1}
                  >
                    <Button
                      label={getText(
                        locId,
                        'whatNextMatchButton',
                        'fpResultsPage'
                      )}
                      onClick={() => {
                        addDataLayerTrigger('button_click', {
                          button_name: 'Start_now',
                        });
                      }}
                      attributes={{
                        value: '',
                        id: 'match-fa-button',
                        'data-testid': 'match-fa-button',
                      }}
                    />
                  </a>
                </div>
              </div>
            )}
            <div className="shareQuiz_container">
              <h3>{getText(locId, 'shareQuizHeading', 'fpResultsPage')}</h3>
              <p>{getText(locId, 'shareQuizMessage', 'fpResultsPage')}</p>
              <div className="action_btns_container">
                <div className="copyLink_action_btn">
                  <CopyLinkButton />
                </div>
                <div className="shareQuiz_action_btn">
                  <p>{getText(locId, 'shareLink', 'fpResultsPage')}</p>
                  <SocialMediaButton
                    icon="/public/images/linkedin-icon.png"
                    socialMediaLink={getText(
                      locId,
                      'linkedInUrl',
                      'fpResultsPage'
                    )}
                    title={getText(
                      locId,
                      'socialMediaTitle',
                      'fpResultsPage'
                    ).replace('%APP_NAME%', 'LinkedIn')}
                  />
                  <SocialMediaButton
                    icon="/public/images/facebook-icon.png"
                    socialMediaLink={getText(
                      locId,
                      'faceBookUrl',
                      'fpResultsPage'
                    )}
                    title={getText(
                      locId,
                      'socialMediaTitle',
                      'fpResultsPage'
                    ).replace('%APP_NAME%', 'Facebook')}
                  />
                  <ShareLinkIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className="footer">
        <div className="disclaimer">
          <p className="microcopy--2">
            {getText(locId, 'resultsPageDisclosure', 'fpResultsPage')}
          </p>
        </div>
      </div>
    </div>
  );
};

FPResultsPage.defaultProps = {
  contactID: '',
  contactName: null,
  financialAdvisorID: '',
  entityID: '',
  timestamp: '',
  callerApp: '',
  userPersona: UserPersonaTypes.DIRECT_URL,
};

FPResultsPage.propTypes = {
  contactID: PropTypes.string,
  contactName: PropTypes.objectOf(PropTypes.string),
  financialAdvisorID: PropTypes.string,
  entityID: PropTypes.string,
  timestamp: PropTypes.string,
  sessionId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  valueList: PropTypes.arrayOf(PropTypes.object).isRequired,
  finalResultsIndex: PropTypes.arrayOf(PropTypes.number).isRequired,
  restartCallback: PropTypes.func.isRequired,
  callerApp: PropTypes.string,
  displaySendToFAButton: PropTypes.bool.isRequired,
  faInfo: PropTypes.shape({
    locale: PropTypes.string,
    environment: PropTypes.string,
    sessionId: PropTypes.string,
    faNo: PropTypes.string,
    fa_entity_id: PropTypes.string,
  }).isRequired,
  financialPlanningContent: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    planningTopics: PropTypes.arrayOf(PropTypes.object),
    investorInvolvementCode: PropTypes.string,
  }),
  userPersona: PropTypes.oneOf(Object.values(UserPersonaTypes)),
};

export default FPResultsPage;
